import React from 'react';
import { CheckIcon, WrenchIcon, HammerIcon, LayoutGridIcon, WarehouseIcon, PackageOpenIcon } from 'lucide-react';
import { useFunnel } from '../../contexts/FunnelContext';
import type { Services } from '../../types/funnel';
import { TextField } from './TextField';
const copy = {
  en: {
    title: 'Extra services',
    sub: 'Choose only what you want. Our team confirms the time and any charge before you book. Nothing is added to your price without your approval.',
    disassembly: ['Disassembly', 'Beds, tables or pieces that must come apart to move.'],
    reassembly: ['Reassembly', 'Put those pieces back together at the new home.'],
    placement: ['Room-by-room placement', 'Furniture placed in the rooms you tell us.'],
    garageOnly: ['Garage-only delivery', 'Some or all items stay in the garage.'],
    unpacking: ['Unpacking', 'Help unpacking boxes at the new home.'],
    garageNote: 'What stays in the garage, and what goes inside?'
  },
  es: {
    title: 'Servicios adicionales',
    sub: 'Elija solo lo que quiera. Nuestro equipo confirma el tiempo y cualquier cargo antes de reservar; no se agrega nada a su precio sin aprobación.',
    disassembly: ['Desarme', 'Camas, mesas o piezas que deben desarmarse.'],
    reassembly: ['Armado', 'Volver a armar esas piezas en el nuevo hogar.'],
    placement: ['Colocación por habitación', 'Muebles colocados en las habitaciones que nos indique.'],
    garageOnly: ['Entrega solo en garaje', 'Algunos o todos los artículos se quedan en el garaje.'],
    unpacking: ['Desempaque', 'Ayuda para desempacar cajas en el nuevo hogar.'],
    garageNote: '¿Qué se queda en el garaje y qué entra a la casa?'
  }
};
type Key = 'disassembly' | keyof Omit<Services, 'garageOnlyNote'>;
const items: {
  key: Key;
  icon: React.ReactNode;
}[] = [{
  key: 'disassembly',
  icon: <WrenchIcon className="h-5 w-5" data-id="element-600" data-mp="3obz4ug8mk54e" />
}, {
  key: 'reassembly',
  icon: <HammerIcon className="h-5 w-5" data-id="element-601" data-mp="3jeqmofnh8135" />
}, {
  key: 'placement',
  icon: <LayoutGridIcon className="h-5 w-5" data-id="element-602" data-mp="3jc4f4g0l5lkx" />
}, {
  key: 'garageOnly',
  icon: <WarehouseIcon className="h-5 w-5" data-id="element-603" data-mp="1na9bmwqm6x3t" />
}, {
  key: 'unpacking',
  icon: <PackageOpenIcon className="h-5 w-5" data-id="element-604" data-mp="1s9lxpf9nw05c" />
}];
export function ServicesPicker() {
  const {
    lang,
    data,
    update
  } = useFunnel();
  const c = copy[lang];
  const isOn = (k: Key) => k === 'disassembly' ? data.needsDisassembly : data.services[k];
  const toggle = (k: Key) => {
    if (k === 'disassembly') update({
      needsDisassembly: !data.needsDisassembly
    });else update({
      services: {
        ...data.services,
        [k]: !data.services[k]
      }
    });
  };
  return <section data-id="element-605" data-mp="19ddt8doh3tb3">
      <h2 className="font-display text-2xl font-black text-magenta" data-id="element-606" data-mp="1eady4l9l6q0m">{c.title}</h2>
      <p className="mb-4 mt-1 max-w-2xl text-sm text-ink-600" data-id="element-607" data-mp="1ecgxvx0jjxij">{c.sub}</p>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-id="element-608" data-mp="1icr2p6clkhvt">
        {items.map(({
        key,
        icon
      }) => {
        const on = isOn(key);
        const [title, body] = c[key];
        return <button key={key} type="button" role="checkbox" aria-checked={on} onClick={() => toggle(key)} className={`flex items-start gap-3 rounded-xl border-2 bg-white p-4 text-left transition-[border-color] duration-150 ease-out ${on ? 'border-lime-dark ring-2 ring-lime' : 'border-ink-200 hover:border-ink-400'}`} data-id="element-609" data-mp="2tptn6chthrl9">
              <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${on ? 'bg-ink text-lime' : 'bg-ink-100 text-ink-600'}`} aria-hidden="true" data-id="element-610" data-mp="4c4hr0ws62qa">
                {icon}
              </span>
              <span className="flex-1" data-id="element-611" data-mp="3qlxjwdt55zd7">
                <span className="block font-semibold text-ink" data-id="element-612" data-mp="3mh7r4xts50wi">{title}</span>
                <span className="mt-0.5 block text-sm leading-snug text-ink-500" data-id="element-613" data-mp="3hj682cwct1l1">{body}</span>
              </span>
              <span className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md border-2 ${on ? 'border-ink bg-lime text-ink' : 'border-ink-300'}`} aria-hidden="true" data-id="element-614" data-mp="1kr5xsuri1524">
                {on && <CheckIcon className="h-4 w-4" strokeWidth={3} data-id="element-615" data-mp="cg48rdc5wmju" />}
              </span>
            </button>;
      })}
      </div>
      {data.services.garageOnly && <TextField className="mt-4" multiline label={c.garageNote} value={data.services.garageOnlyNote} onChange={v => update({
      services: {
        ...data.services,
        garageOnlyNote: v.slice(0, 300)
      }
    })} data-id="element-616" data-mp="3fxv9rggrmko" />}
    </section>;
}